import { QueryClient } from "@tanstack/react-query";
import { toast } from "react-toastify";

/**
 * Create a base query client to be used throughout the app
 */
export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      retry: 1,
      staleTime: 5 * 60 * 1000, // 5 minutes
    },
  },
});

/**
 * Helper function for making API requests
 *
 * @param {string} url - API endpoint
 * @param {Object} options - Fetch API options
 * @returns {Promise} - Fetch response
 */
export async function apiRequest(url, options = {}, authorized = true) {
  const baseUrl = process.env.REACT_APP_API_URL || "";
  const fullUrl =
    baseUrl.endsWith("/") || url.startsWith("/")
      ? `${baseUrl}${url}`
      : `${baseUrl}/${url}`;
  const token = localStorage.getItem("authToken");
  const isFormData = options?.body instanceof FormData;

  const fetchOptions = {
    ...options,
    headers: {
      ...(isFormData ? {} : { "Content-Type": "application/json" }),
      ...(options.headers || {}),
      ...(authorized ? { Authorization: `Bearer ${token}` } : {}),
      user : "admin"
    },
  };

  try {
    const response = await fetch(fullUrl, fetchOptions);
    const data = await response.json().catch(() => ({}));
    if (data.error) {
      const message =
        data.message || response.statusText || "Something went wrong";
      throw new Error(message);
    }
    return data;
  } catch (error) {
    if (!error.silent) {
      toast.error(error.message || "Something went wrong");
    }
    throw error;
  }
}

/**
 * Helper function for extracting query parameters
 *
 * @param {Object} params - Query parameters object
 * @returns {string} - Query string
 */
export function buildQueryString(params) {
  return Object.entries(params)
    .filter(
      ([_, value]) => value !== undefined && value !== null && value !== ""
    )
    .map(
      ([key, value]) =>
        `${encodeURIComponent(key)}=${encodeURIComponent(value)}`
    )
    .join("&");
}

/**
 * Default fetch function for react-query
 *
 * @param {string} url - API endpoint
 * @returns {Promise} - Fetch response
 */
export async function defaultFetcher(url) {
  return apiRequest(url);
}

/**
 * Helper function for invalidating queries
 *
 * @param {string|Array} queryKey - Query key to invalidate
 */
export function invalidateQueries(queryKey) {
  return queryClient.invalidateQueries({ queryKey });
}

export default queryClient;
